import type { CSSProperties } from "react";
import { useEffect, useState } from "react";

const ROSTER_CARD_MAX_HEIGHT_PX = 520;
export const ROSTER_WINDOW_TRANSITION_MS = 180;
const RIGHT_MOUSE_BUTTON = 2;
const VIEWPORT_MARGIN_PX = 16;
const PANEL_MENU_GAP_PX = 16;
const DEFAULT_TOP_SAFE_AREA_PX = 96;
const CONTAINER_VERTICAL_PADDING_PX = 8; // matches p-2 on FloatingWindowRenderer containerClassName
const ROSTER_PANEL_MAX_VIEWPORT_WIDTH_RATIO = 0.8;
const ROSTER_IGNORE_CLICK_SELECTORS = [
  "[data-people-button]",
  "[data-roster]",
  "[data-avatar-menu]",
] as const;

interface UseRosterWindowProps {
  avatarMenuOpen: boolean;
  onClose: () => void;
  open: boolean;
}

interface ViewportSize {
  width: number;
  height: number;
}

interface RosterWindowLayout {
  panelStyle: CSSProperties;
  positionStyle: CSSProperties;
}

const getViewportSize = (): ViewportSize => ({
  height: window.innerHeight,
  width: window.innerWidth,
});

const shouldIgnoreOutsideClick = ({
  avatarMenuOpen,
  event,
}: {
  avatarMenuOpen: boolean;
  event: MouseEvent;
}) => {
  if (event.button === RIGHT_MOUSE_BUTTON) {
    return true;
  }

  if (avatarMenuOpen) {
    return true;
  }

  const { target } = event;

  if (!(target instanceof Element)) {
    return true;
  }

  return ROSTER_IGNORE_CLICK_SELECTORS.some((selector) =>
    target.closest(selector)
  );
};

const getRosterWindowLayout = (
  viewportSize: ViewportSize
): RosterWindowLayout => {
  const rightSideMenuRect = document
    .querySelector("[data-right-side-menu]")
    ?.getBoundingClientRect();
  const bottomMenuRect = document
    .querySelector("[data-bottom-menu]")
    ?.getBoundingClientRect();
  const fallbackWidthPx = viewportSize.width - VIEWPORT_MARGIN_PX * 2;
  const panelWidthPx = Math.min(
    viewportSize.width * ROSTER_PANEL_MAX_VIEWPORT_WIDTH_RATIO,
    fallbackWidthPx
  );
  const top = Math.max(
    DEFAULT_TOP_SAFE_AREA_PX,
    rightSideMenuRect?.top ?? DEFAULT_TOP_SAFE_AREA_PX
  );
  const right = rightSideMenuRect
    ? viewportSize.width - rightSideMenuRect.left + PANEL_MENU_GAP_PX
    : VIEWPORT_MARGIN_PX;
  const safePanelWidthPx = Math.max(0, panelWidthPx);
  const maxHeight = bottomMenuRect
    ? Math.max(
        0,
        bottomMenuRect.top -
          top -
          VIEWPORT_MARGIN_PX -
          CONTAINER_VERTICAL_PADDING_PX * 2
      )
    : ROSTER_CARD_MAX_HEIGHT_PX;

  return {
    panelStyle: {
      maxHeight: `${maxHeight}px`,
      width: `${safePanelWidthPx}px`,
    },
    positionStyle: {
      right: `${right}px`,
      top: `${top}px`,
    },
  };
};

const useRosterWindowPresence = (open: boolean) => {
  const [isRendered, setIsRendered] = useState(open);
  const [isVisible, setIsVisible] = useState(open);

  useEffect(() => {
    if (open) {
      let firstAnimationFrame = 0;
      let secondAnimationFrame = 0;

      setIsRendered(true);
      firstAnimationFrame = requestAnimationFrame(() => {
        secondAnimationFrame = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });

      return () => {
        cancelAnimationFrame(firstAnimationFrame);
        cancelAnimationFrame(secondAnimationFrame);
      };
    }

    setIsVisible(false);
    const timer = window.setTimeout(() => {
      setIsRendered(false);
    }, ROSTER_WINDOW_TRANSITION_MS);

    return () => window.clearTimeout(timer);
  }, [open]);

  return { isRendered, isVisible };
};

const useRosterWindowDismiss = ({
  avatarMenuOpen,
  onClose,
  open,
}: UseRosterWindowProps) => {
  useEffect(() => {
    if (!open) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (shouldIgnoreOutsideClick({ avatarMenuOpen, event })) {
        return;
      }

      onClose();
    };

    window.addEventListener("click", handleClickOutside);

    return () => {
      window.removeEventListener("click", handleClickOutside);
    };
  }, [avatarMenuOpen, onClose, open]);
};

const useViewportSize = () => {
  const [viewportSize, setViewportSize] = useState<ViewportSize>(() =>
    typeof window === "undefined" ? { height: 0, width: 0 } : getViewportSize()
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleResize = () => {
      setViewportSize(getViewportSize());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return viewportSize;
};

const useRosterWindowLayout = (enabled: boolean) => {
  const viewportSize = useViewportSize();

  if (!enabled || typeof window === "undefined") {
    return null;
  }

  return getRosterWindowLayout(viewportSize);
};

export const useRosterWindow = ({
  avatarMenuOpen,
  onClose,
  open,
}: UseRosterWindowProps) => {
  const { isRendered, isVisible } = useRosterWindowPresence(open);

  useRosterWindowDismiss({
    avatarMenuOpen,
    onClose,
    open,
  });

  return {
    isRendered,
    isVisible,
    layout: useRosterWindowLayout(isRendered),
  };
};
